import { PlanType } from '@/types/plan';

export enum VisaType {
  EMPLOYMENT = 'EMPLOYMENT',
  STUDY = 'STUDY',
}

export const mapVisaIconType: Record<VisaType, string> = {
  [VisaType.STUDY]: '/study.svg',
  [VisaType.EMPLOYMENT]: '/employment.svg',
};

export enum SessionType {
  GUIDE = 'GUIDE',
  COMMENTS = 'COMMENTS',
  PROFILES = 'PROFILES',
  JOBS = 'JOBS',
  ACCOMMODATIONS = 'ACCOMMODATIONS',
  ACCOMMODATIONS_WEBSITE = 'ACCOMMODATIONS_WEBSITE',
  SCAMS = 'SCAMS',
  FAMILY = 'FAMILY',
  PET = 'PET',
  PROFESSION = 'PROFESSION',
}

export enum ReportStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  FINISHED = 'FINISHED',
  FAILED = 'FAILED',
}

export interface AccommodationsWebsite {
  company: string;
  description: string;
  link: string;
  imageUrl?: string | undefined;
}

export interface Accommodations {
  type: string;
  description: string;
}

export interface Comments {
  author: string;
  quote: string;
  link: string;
}

export interface Family {
  content: string;
  expectedDuration: string;
  source: string;
}

export interface Guide {
  title: string;
  content: string;
  expectedDuration: string;
  source: string;
}

export interface Jobs {
  website: string;
  summary: string;
  link: string;
}

export interface Pet {
  content: string;
  expectedDuration: string;
  source: string;
}

export interface Profession {
  session: string;
  content: string;
  source: string;
}

export interface Profiles {
  channel: string;
  description: string;
  link: string;
  profileImage?: string | undefined;
}

export interface Scams {
  title: string;
  description: string;
  howToAvoid: string;
}

export type SessionContent =
  | AccommodationsWebsite[]
  | Accommodations[]
  | Comments[]
  | Family[]
  | Guide[]
  | Jobs[]
  | Pet[]
  | Profession[]
  | Profiles[]
  | Scams[];

export interface Session {
  id: string;
  order: number;
  type: SessionType;
  content: SessionContent;
  createdAt: Date;
  updatedAt: Date;
}

export interface Report {
  id: string;
  targetDestination: string;
  citizenship: string;
  visaType: VisaType;
  userId: string;
  language: string;
  createdAt: Date;
  familyMembers?: string[] | undefined;
  familyMembersCitizenship?: string | undefined;
  pet?: string[] | undefined;
  professionQuestion?: string | undefined;
  planType: PlanType;
  sessions: Session[];
  updatedAt: Date;
  status: ReportStatus;
}

export interface RequestReportFormData {
  language?: string;
  targetDestination: string;
  visaType: VisaType;
  citizenship: string;
  familyMembers?: string[] | undefined;
  familyMembersCitizenship?: string | undefined;
  pet?: string[] | undefined;
  professionQuestion?: string | undefined;
}

export type SubmitRequestReportFormData = Omit<
  RequestReportFormData,
  'familyMembers' | 'pet'
> & {
  familyMembers?: string;
  pet?: string;
};

export const mapPetTranslationKeys: Record<string, string> = {
  dog: 'new_report.questions.pet.options.dog',
  cat: 'new_report.questions.pet.options.cat',
};

export const mapFamilyMembersTranslationKeys: Record<string, string> = {
  spouse_legally_married:
    'new_report.questions.family_members.options.spouse_legally_married',
  spouse_civil_union:
    'new_report.questions.family_members.options.spouse_civil_union',
  children_underage:
    'new_report.questions.family_members.options.children_underage',
  children_disability:
    'new_report.questions.family_members.options.children_disability',
  children_overage_single_student:
    'new_report.questions.family_members.options.children_overage_single_student',
  parents: 'new_report.questions.family_members.options.parents',
};
